/**
 * Hosted Agent Manifest
 *
 * Hosted agents use the standard Agent action shape, but execute in an Azure AI
 * Foundry hosted container. Their authoring inputs flow through ParameterList
 * so they share the same field and dynamic-content behavior as other agents.
 */

import type { OperationManifest } from '@/types/operationManifest';
import agentLoopManifest from './agentloop';

const baseInputProperties = agentLoopManifest.properties.inputs?.properties ?? {};

export default {
  properties: {
    ...agentLoopManifest.properties,
    summary: 'Hosted Agent',
    description:
      'Agent loop that runs in an Azure AI Foundry hosted container and returns generated files alongside the assistant message.',
    inputs: {
      type: 'object',
      properties: {
        agentModelType: {
          ...baseInputProperties.agentModelType,
          type: 'string',
          default: 'FoundryHostedAgent',
          'x-ms-visibility': 'hideInUI',
        },
        _systemMessage: baseInputProperties._systemMessage,
        _userMessage: baseInputProperties._userMessage,
        inputFiles: {
          type: 'array',
          title: 'Input Files',
          description: 'Files to upload to the hosted agent workspace before the agent runs.',
          'x-ms-visibility': 'important',
          items: {
            type: 'object',
            properties: {
              name: {
                type: 'string',
                title: 'File Name',
                'x-ms-summary': 'File Name',
                description: 'Name of the file in the agent workspace (e.g., data.csv, report.pdf).',
              },
              content: {
                type: 'string',
                title: 'Content',
                'x-ms-summary': 'Content',
                description: 'File content. Use an expression to reference previous action outputs.',
              },
              isSkill: {
                type: 'boolean',
                title: 'Use as skill',
                'x-ms-summary': 'Use as skill',
                description: 'Stage the file as a Copilot skill instead of a plain workspace file.',
              },
            },
            required: ['name', 'content'],
          },
        },
      },
    },
    outputs: {
      type: 'object',
      properties: {
        ...agentLoopManifest.properties.outputs?.properties,
        fileOutputs: {
          type: 'array',
          title: 'File Outputs',
          description: 'Files produced during hosted agent execution',
          items: {
            type: 'object',
            properties: {
              path: {
                type: 'string',
                title: 'Path',
                description: 'File path relative to the agent session workspace',
              },
              content: {
                title: 'Content',
                description:
                  'File content — a plain string for text files, or a content envelope object ({"$content-type":"...","$content":"base64..."}) for binary files.',
              },
              size: {
                type: 'integer',
                title: 'Size',
                description: 'File size in bytes',
              },
            },
          },
        },
      },
    },
  },
} as OperationManifest;
